import type { PartnerCockpitNotificationType } from '@houzy/schemas/partner-cockpit/notifications';
import type { OnboardingStep } from '@houzy/schemas/partner-cockpit/onboarding';
import type { EngagementContactRequestType } from '@houzy/schemas/partner-cockpit/partner-matching';

import { mixpanelClient } from './mixpanelClient';
import { useMixpanel } from './mixpanelContext';

// Every Mixpanel event the cockpit sends, so dashboards and code cannot drift apart on a name or a property. Each
// carries only what its metric cannot do without; `partnerUuid` is added to all of them once the user is known.

export type CheckoutSource = 'ops-link' | 'self-onboarding';

export type OfferRequestContactShortcut = 'Phone' | 'Email';

// A lead without a matching cannot take a contact request, so its partner reaching for the phone or e-mail link on
// the lead is what counts as contacting the customer.
export type OfferRequestContactType = EngagementContactRequestType | OfferRequestContactShortcut;

type NoProperties = Record<string, never>;

export type OfferRequestList = 'new' | 'open';

export type LoginMethod = 'password' | 'magicLink';

// The offer request's reference id rather than the lead's uuid: one request reaches a partner as a matching or an
// assignment, each with its own uuid, and the reference id is also what BackOps and Metabase look requests up by.
type OfferRequestEvent = { offerRequestId: string };

export type TrackingEventProperties = {
    'Checkout Loaded': { checkoutSource: CheckoutSource };
    'Checkout Submitted': { checkoutSource: CheckoutSource };
    'Checkout Completed': { checkoutSource: CheckoutSource };
    'Onboarding Link Requested': NoProperties;
    'Onboarding Started': NoProperties;
    // The subscription step has no save of its own: its completion is `Checkout Completed` from the onboarding source.
    'Onboarding Step Completed': { step: OnboardingStep };
    // Listing happens on the backend, so the time since is carried here rather than tracked as an event of its own.
    // `isFirstOpening` is per partner, across team members and the assignment's e-mail link.
    'Offer Request Opened': OfferRequestEvent & {
        minutesSinceListed: number;
        isFirstOpening: boolean;
        list: OfferRequestList;
    };
    'Offer Request Contact Initialised': OfferRequestEvent & {
        minutesSinceListed: number;
        contactType: OfferRequestContactType;
    };
    'Offer Request Contact Revealed': OfferRequestEvent & {
        contactType: OfferRequestContactShortcut;
        customerAccepted: boolean;
    };
    'Offer Request Declined': OfferRequestEvent;
    'Offer Request Chat Message Sent': OfferRequestEvent;
    'Offer Request Status Changed': OfferRequestEvent & { list: OfferRequestList };
    'Offer Request Filter Used': { filter: string };
    // `partnerUuid` is passed here because the login itself is what sets it on later events.
    'Logged In': { method: LoginMethod; partnerUuid: string | undefined };
    'Notifications Opened': NoProperties;
    'Settings Changed': { setting: string };
    'Verification Submitted': NoProperties;
    // From the verification settings page and the onboarding validation step alike.
    'Verification Document Uploaded': NoProperties;
    'Notification Clicked': { notificationType: PartnerCockpitNotificationType };
};

export type TrackingEventName = keyof TrackingEventProperties;

export type TrackEvent = <E extends TrackingEventName>(event: E, properties: TrackingEventProperties[E]) => void;

export function useTrackEvent(): TrackEvent {
    return useMixpanel().track;
}

// For code outside React, such as route guards. Components use `useTrackEvent` so tests can swap the client.
export const trackEvent: TrackEvent = (event, properties) => mixpanelClient.track(event, properties);
